<template>
  <div class="flex flex-col h-full overflow-hidden bg-white">
    <div v-if="loadingForm" class="flex items-center justify-center h-full">
      <loader />
    </div>

    <template v-else-if="form">
      <!-- Header -->
      <div class="flex items-center gap-3 p-4 border-b bg-white flex-none">
        <div class="flex flex-col">
          <span class="text-sm text-surface-500">{{ submission.customerName }} | {{ formatDate(submission.submittedAt) }}</span>
        </div>
      </div>

      <!-- Form Fields (read-only) -->
      <div class="flex-1 overflow-y-auto bg-surface-50">
        <div class="px-4 py-6 space-y-6 max-w-2xl mx-auto">
          <div v-for="el in form.elements" :key="el.id">
            <!-- Short Text -->
            <div v-if="el.type === 'short_text'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
              </label>
              <div class="w-full p-3 bg-white border border-surface-200 rounded-xl text-sm text-surface-800">
                {{ submission.data[el.id] || '-' }}
              </div>
            </div>

            <!-- Checkboxes -->
            <div v-else-if="el.type === 'checkboxes'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl shadow-sm"
                :class="{ '!border-primary-300 !bg-primary-50/30': submission.data[`${el.id}_${opt}`] }"
              >
                <Checkbox
                  :modelValue="!!submission.data[`${el.id}_${opt}`]"
                  :binary="true"
                  disabled
                />
                <span class="text-sm font-medium text-surface-700 flex-1">{{ opt }}</span>
              </label>
            </div>

            <!-- Multiple Choice -->
            <div v-else-if="el.type === 'multiple_choice'" class="space-y-3">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
              </label>
              <label
                v-for="(opt, i) in el.options"
                :key="i"
                class="flex items-center gap-3 p-4 bg-white border border-surface-200 rounded-2xl shadow-sm"
                :class="{ '!border-primary-300 !bg-primary-50/30': submission.data[el.id] === opt }"
              >
                <RadioButton
                  :modelValue="submission.data[el.id]"
                  :value="opt"
                  :name="el.id"
                  disabled
                />
                <span class="text-sm font-medium text-surface-700">{{ opt }}</span>
              </label>
            </div>

            <!-- Read Only -->
            <div v-else-if="el.type === 'read_only'" class="mb-4">
              <label class="block text-sm font-bold text-surface-800 mb-2">{{ el.label }}</label>
              <div class="bg-surface-100/50 p-5 rounded-2xl border border-surface-200 text-sm text-surface-600 leading-relaxed whitespace-pre-wrap">
                {{ el.content }}
              </div>
            </div>

            <!-- Signature -->
            <div v-else-if="el.type === 'signature'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
              </label>
              <div class="bg-white border border-surface-200 rounded-2xl p-4">
                <img
                  v-if="submission.signatureDataUrl"
                  :src="submission.signatureDataUrl"
                  class="max-w-[200px] h-auto"
                  alt="חתימה"
                />
                <span v-else class="text-sm text-surface-400">לא נחתם</span>
              </div>
            </div>

            <!-- Image -->
            <div v-else-if="el.type === 'image'">
              <label class="block text-sm font-bold text-surface-800 mb-1.5">
                {{ el.label }}
              </label>
              <div class="bg-white border border-surface-200 rounded-2xl p-4">
                <a
                  v-if="submission.data[el.id]"
                  :href="submission.data[el.id]"
                  target="_blank"
                  rel="noopener"
                >
                  <img
                    :src="submission.data[el.id]"
                    class="max-w-full max-h-72 rounded-xl"
                    alt="תמונה"
                  />
                </a>
                <span v-else class="text-sm text-surface-400">לא הועלתה תמונה</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Fallback if form couldn't load -->
    <div v-else class="flex flex-col items-center justify-center h-full gap-2 text-surface-400">
      <i class="pi pi-exclamation-circle text-3xl"></i>
      <span class="text-sm">לא ניתן לטעון את הטופס</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, type PropType } from 'vue';
import { DateTime } from 'luxon';
import type { IConsentForm, IConsentFormSubmission } from '@torim-app/types';
import { ConsentFormsApi } from '@app/api/admin/consent-forms-api';

const props = defineProps({
  submission: {
    type: Object as PropType<IConsentFormSubmission>,
    required: true,
  },
});

const form = ref<IConsentForm | null>(null);
const loadingForm = ref(true);

const formatDate = (date: Date | string) => {
  return DateTime.fromISO(String(date)).toLocaleString(DateTime.DATETIME_MED);
};

onMounted(async () => {
  try {
    form.value = await ConsentFormsApi.fetchConsentFormById(props.submission.formId);
  } catch (e) {
    console.error('Could not load form definition:', e);
  } finally {
    loadingForm.value = false;
  }
});
</script>
